import {Modal} from '@mantine/core';
import {useQueryClient} from '@tanstack/react-query';
import type {FC} from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ReportType} from '../../../../../core/entities/reportAnalytics/schema';
import {convertStringToReportType} from '../../../../../core/factories/reports/convertStringToReportType';
import {
  useGetEntryManagement,
  useGetEntryManagements,
  useReportAnalyzeStep,
} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {
  useGetCompaniesInSummary,
  useGetDepartmentsInSummary,
} from '../../../../../core/repositories/reportSummaryRepository';
import {showErrorSnackBar} from '../../../../../shared/components';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {ReportTypeLabel} from '../../../../reports/components/Container/Presenter/ReportTypeLabel';
import {Z_INDEX} from '../../../constants/zIndex';
import {useAiTodoEdit} from '../../../usecase/useAiTodoEdit';
import {generateCandidateId} from '../../../usecase/useAiTodoGeneration';
import {useAiTodoGenerationFlow} from '../../../usecase/useAiTodoGenerationFlow';
import {useAiTodoSave} from '../../../usecase/useAiTodoSave';
import {
  type StepCandidate,
  findExistingEntry,
  useEntryStepCreation,
} from '../../../usecase/useEntryStepCreation';
import {AiGeneratedTodosModal} from '../Presenter/AiGeneratedTodosModal';
import {AiTodoCompanySelectContent} from '../Presenter/Progress/AiTodoCompanySelectContent';
import {AiTodoDepartmentSelectContent} from '../Presenter/Progress/AiTodoDepartmentSelectContent';
import {
  AiTodoProcessingContent,
  PROCESSING_STATE,
} from '../Presenter/Progress/AiTodoProcessingContent';
import {AiTodoStepSelectContent} from '../Presenter/Progress/AiTodoStepSelectContent';
import {DepartmentSelectItem} from '../Presenter/Progress/DepartmentSelectItem';
import {OpenTodoPageConfirmModal} from '../Presenter/Progress/OpenTodoPageConfirmModal';

type Props = {
  opened: boolean;
  onClose: () => void;
  // 以下が全て渡されたら企業/部門選択をスキップして自動開始
  companyID?: number;
  companyName?: string;
  departmentID?: number;
  reportType?: ReportType;
  // 部門の初期選択値（`${departmentID}-${reportType}` 形式）
  initialDepartment?: string;
  // false の場合、ステップ選択をスキップして最初のステップで自動生成
  enableStepSelect?: boolean;
};

const MODAL_STEP = {
  COMPANY_SELECT: 'companySelect',
  DEPARTMENT_SELECT: 'departmentSelect',
  PROCESSING: 'processing',
  STEP_SELECT: 'stepSelect',
  GENERATING: 'generating',
  RESULT: 'result',
} as const;

type ModalStep = (typeof MODAL_STEP)[keyof typeof MODAL_STEP];

export const AiTodoGenerateWithEntryMgtCreationContainer: FC<Props> = ({
  opened,
  onClose,
  companyID: propsCompanyID,
  companyName: propsCompanyName,
  departmentID: propsDepartmentID,
  reportType: propsReportType,
  initialDepartment: propsInitialDepartment,
  enableStepSelect = true,
}) => {
  // Pre-filledモード（企業/部門がProps確定済み）
  const isPreFilled =
    propsCompanyID !== undefined &&
    propsCompanyName !== undefined &&
    propsDepartmentID !== undefined &&
    propsReportType !== undefined;

  // 企業pre-selectモード（企業のみProps確定済み、部門選択から始める）
  const isCompanyPreSelected =
    !isPreFilled &&
    propsCompanyID !== undefined &&
    propsCompanyName !== undefined;

  // Pre-filledモード自動開始済みフラグ
  const hasStartedPreFillRef = useRef(false);

  const queryClient = useQueryClient();
  const {isPC = true} = useMediaQuery();

  // モーダルステップ
  const [modalStep, setModalStep] = useState<ModalStep>(
    MODAL_STEP.COMPANY_SELECT,
  );

  // 企業選択
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(
    isCompanyPreSelected ? (propsCompanyID as number) : 0,
  );
  const [selectedCompanyName, setSelectedCompanyName] = useState(
    isCompanyPreSelected ? (propsCompanyName as string) : '',
  );

  // 部門選択
  const [selectedDepartment, setSelectedDepartment] = useState(
    isCompanyPreSelected ? (propsInitialDepartment ?? '') : '',
  );

  // ステップ選択
  const [selectedStep, setSelectedStep] = useState('');

  // --- API Hooks ---

  // 企業一覧（pre-filledモード・企業pre-selectモードでは不要）
  // report_summariesにデータがある企業のみ取得（部門が空になるケースを防止）
  const {data: companiesData} = useGetCompaniesInSummary({
    queryParams: {},
    config: {
      enabled: opened && !isPreFilled && !isCompanyPreSelected,
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: 30 * 60 * 1000,
    },
  });

  // 部門一覧（selection）（pre-filledモードでは不要）
  const {data: selectionDepts} = useGetDepartmentsInSummary({
    companyID: selectedCompanyId,
    queryParams: {reportType: 'selection'},
    config: {enabled: opened && selectedCompanyId > 0 && !isPreFilled},
  });

  // 部門一覧（intern）（pre-filledモードでは不要）
  const {data: internDepts} = useGetDepartmentsInSummary({
    companyID: selectedCompanyId,
    queryParams: {reportType: 'intern'},
    config: {enabled: opened && selectedCompanyId > 0 && !isPreFilled},
  });

  // 既存の選考管理を企業名で検索
  const {
    data: entryManagementsData,
    refetch: refetchEntryManagements,
    isLoading: isLoadingEntryManagements,
  } = useGetEntryManagements({
    companyName: selectedCompanyName,
    limit: 100,
    config: {enabled: opened && selectedCompanyName.length > 0},
  });

  // 既存の選考管理IDを取得（companyIDとcompanyNameの両方でチェック）
  // キャッシュされたデータを使用してパフォーマンス最適化
  // ※usecase側でも最新データで再チェックし、キャッシュが古い場合の500エラーを防止
  const existingEntryManagementId = useMemo(() => {
    if (!entryManagementsData?.ok) return null;
    const entry = findExistingEntry(
      entryManagementsData.value.entries,
      selectedCompanyId,
      selectedCompanyName,
    );
    return entry?.id ?? null;
  }, [entryManagementsData, selectedCompanyId, selectedCompanyName]);

  // 詳細APIで全ステップを取得
  const {data: entryDetailData, isLoading: isLoadingEntryDetail} =
    useGetEntryManagement(existingEntryManagementId ?? 0, {
      config: {enabled: opened && existingEntryManagementId !== null},
    });

  const reportAnalyzeMutation = useReportAnalyzeStep();

  // --- Custom Hooks ---

  // ステップ追加処理
  const {
    processingState,
    setProcessingState,
    addedStepCount,
    totalStepCount,
    addedSteps,
    userEntryManagementId,
    addAllSteps,
    resetStepCreation,
  } = useEntryStepCreation({
    queryClient,
    refetchEntryManagements,
    existingEntryManagementId,
    entryDetailData,
  });

  // addAllSteps実行中フラグ（二重呼び出し防止）
  const isAddingStepsRef = useRef(false);

  // TODO編集処理
  const {
    candidates,
    setCandidates,
    editState,
    startEdit,
    cancelEdit,
    updateCandidate,
    resetEditState,
  } = useAiTodoEdit();

  // リセット関数
  const resetAllStates = useCallback(() => {
    if (isCompanyPreSelected) {
      setModalStep(MODAL_STEP.DEPARTMENT_SELECT);
      setSelectedCompanyId(propsCompanyID as number);
      setSelectedCompanyName(propsCompanyName as string);
    } else {
      setModalStep(MODAL_STEP.COMPANY_SELECT);
      setSelectedCompanyId(0);
      setSelectedCompanyName('');
    }
    setSelectedDepartment(
      isCompanyPreSelected ? (propsInitialDepartment ?? '') : '',
    );
    setSelectedStep('');
    resetStepCreation();
    resetEditState();
    resetSaveState();
    resetGenerationFlow();
    hasStartedPreFillRef.current = false;
    isAddingStepsRef.current = false;
  }, [
    isCompanyPreSelected,
    propsCompanyID,
    propsCompanyName,
    propsInitialDepartment,
    resetStepCreation,
    resetEditState,
  ]);

  // TODO生成処理
  const {
    isGeneratingInBackgroundRef,
    hasAutoGeneratedRef,
    handleStepSubmit: handleStepSubmitFlow,
    startBackgroundGeneration,
    resetGenerationFlow,
  } = useAiTodoGenerationFlow({
    onGenerationSuccess: (todos) => {
      // 各候補にユニークIDを付与
      const candidatesWithId = todos.map((todo, index) => ({
        ...todo,
        candidateId: generateCandidateId(index),
      }));
      setCandidates(candidatesWithId);
      setModalStep(MODAL_STEP.RESULT);
    },
    onGenerationError: (wasBackground) => {
      if (wasBackground) {
        resetAllStates();
      } else {
        setModalStep(MODAL_STEP.STEP_SELECT);
      }
    },
    resetAllStates,
  });

  // TODO保存処理
  const {
    saveState,
    handleSaveTodo: handleSaveTodoHook,
    handleSaveAllTodos: handleSaveAllTodosHook,
    isTodoSaved,
    resetSaveState,
    showOpenTodoPageConfirm,
    handleOpenTodoPageConfirm,
    handleCloseOpenTodoPageConfirm,
  } = useAiTodoSave({
    userEntryManagementId,
    isPC,
  });

  // --- 企業リストの変換 ---
  const companyList = useMemo(() => {
    if (!companiesData?.ok) return [];

    return companiesData.value.companies.map((company) => ({
      id: company.companyID,
      value: company.companyName,
    }));
  }, [companiesData]);

  // --- 部門リストの変換 ---
  const departmentOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
      reportType: ReportType;
    }[] = [];

    if (selectionDepts?.ok && selectionDepts.value.departments) {
      options.push(
        ...selectionDepts.value.departments.map((dept) => ({
          value: `${dept.departmentID}-selection`,
          label: dept.departmentName,
          reportType: 'selection' as const,
        })),
      );
    }

    if (internDepts?.ok && internDepts.value.departments) {
      options.push(
        ...internDepts.value.departments.map((dept) => ({
          value: `${dept.departmentID}-intern`,
          label: dept.departmentName,
          reportType: 'intern' as const,
        })),
      );
    }

    return options;
  }, [selectionDepts, internDepts]);

  // --- ステップ選択肢 ---
  const stepOptions = useMemo(() => {
    return addedSteps.map((step) => ({
      value: String(step.id),
      label: step.stepName,
    }));
  }, [addedSteps]);

  // --- 選択済みラベル ---
  const selectedDepartmentLabel = useMemo(() => {
    if (!selectedDepartment) return '';
    const option = departmentOptions.find(
      (o) => o.value === selectedDepartment,
    );
    if (!option) return '';
    const typeLabel = option.reportType === 'intern' ? 'インターン' : '本選考';
    return `${option.label}（${typeLabel}）`;
  }, [selectedDepartment, departmentOptions]);

  const selectedStepLabel = useMemo(() => {
    if (!selectedStep) return '';
    const option = stepOptions.find((o) => o.value === selectedStep);
    return option?.label ?? '';
  }, [selectedStep, stepOptions]);

  const leftSectionBadge = selectedDepartment ? (
    <ReportTypeLabel
      type={convertStringToReportType(
        selectedDepartment.split('-')[1] as ReportType,
      )}
    />
  ) : null;

  // 企業pre-selectモード: モーダルが開いたら部門選択から始める
  useEffect(() => {
    if (!opened || !isCompanyPreSelected) return;
    setModalStep(MODAL_STEP.DEPARTMENT_SELECT);
    setSelectedCompanyId(propsCompanyID as number);
    setSelectedCompanyName(propsCompanyName as string);
    setSelectedDepartment(propsInitialDepartment ?? '');
  }, [
    opened,
    isCompanyPreSelected,
    propsCompanyID,
    propsCompanyName,
    propsInitialDepartment,
  ]);

  // モーダルが閉じられたらリセット（バックグラウンド生成中はリセットしない）
  useEffect(() => {
    if (!opened && !isGeneratingInBackgroundRef.current) {
      resetAllStates();
    }
  }, [opened, resetAllStates, isGeneratingInBackgroundRef]);

  // --- ハンドラ ---

  const handleClose = useCallback(() => {
    if (modalStep === MODAL_STEP.GENERATING) {
      // 生成中に閉じた場合はバックグラウンドで継続
      startBackgroundGeneration();
      onClose();
    } else {
      resetAllStates();
      onClose();
    }
  }, [modalStep, onClose, resetAllStates, startBackgroundGeneration]);

  // 企業選択完了
  const handleCompanySubmit = useCallback(() => {
    if (selectedCompanyId > 0 && selectedCompanyName.trim()) {
      setModalStep(MODAL_STEP.DEPARTMENT_SELECT);
    }
  }, [selectedCompanyId, selectedCompanyName]);

  // 部門選択画面から企業選択画面へ戻る
  const handleBackToCompanySelect = useCallback(() => {
    setSelectedCompanyId(0);
    setSelectedCompanyName('');
    setSelectedDepartment('');
    setSelectedStep('');
    reportAnalyzeMutation.reset();
    resetStepCreation();
    resetGenerationFlow();
    setModalStep(MODAL_STEP.COMPANY_SELECT);
  }, [reportAnalyzeMutation, resetStepCreation, resetGenerationFlow]);

  // ステップ選択画面から部門選択画面へ戻る
  const handleBackToDepartmentSelect = useCallback(() => {
    setSelectedDepartment('');
    setSelectedStep('');
    reportAnalyzeMutation.reset();
    resetStepCreation();
    resetGenerationFlow();
    setModalStep(MODAL_STEP.DEPARTMENT_SELECT);
  }, [reportAnalyzeMutation, resetStepCreation, resetGenerationFlow]);

  // 部門選択完了 → 処理開始
  const handleDepartmentSubmit = useCallback(async () => {
    if (!selectedDepartment) return;

    const [deptIdStr, reportType] = selectedDepartment.split('-');
    const departmentID = Number(deptIdStr);

    setModalStep(MODAL_STEP.PROCESSING);
    setProcessingState(PROCESSING_STATE.FETCHING_STEPS);

    try {
      // ステップ候補を取得
      const result = await reportAnalyzeMutation.mutateAsync({
        companyID: selectedCompanyId,
        departmentID,
        reportType: reportType as ReportType,
      });

      if (!result.ok || result.value.candidates.length === 0) {
        setProcessingState(PROCESSING_STATE.ERROR);
        return;
      }

      const stepCandidates: StepCandidate[] = result.value.candidates;

      // 選考管理データのロード待ち
      if (isLoadingEntryManagements || isLoadingEntryDetail) {
        setProcessingState(PROCESSING_STATE.CHECKING_ENTRY);
        return;
      }

      // 二重呼び出し防止チェック
      if (isAddingStepsRef.current) {
        return;
      }

      isAddingStepsRef.current = true;
      try {
        const addResult = await addAllSteps(
          stepCandidates,
          selectedCompanyId,
          selectedCompanyName,
          departmentID,
          reportType as ReportType,
        );

        if (addResult.shouldProceedToStepSelect) {
          setModalStep(MODAL_STEP.STEP_SELECT);
        }
      } finally {
        isAddingStepsRef.current = false;
      }
    } catch (_error) {
      setProcessingState(PROCESSING_STATE.ERROR);
    }
  }, [
    selectedDepartment,
    selectedCompanyId,
    selectedCompanyName,
    reportAnalyzeMutation,
    isLoadingEntryManagements,
    isLoadingEntryDetail,
    addAllSteps,
    setProcessingState,
  ]);

  // ステップ選択からTODO生成
  const handleStepSubmit = useCallback(
    async (overrideStepId?: number) => {
      const stepId = overrideStepId ?? Number(selectedStep);
      setModalStep(MODAL_STEP.GENERATING);
      await handleStepSubmitFlow(stepId, selectedDepartment, overrideStepId);
    },
    [selectedStep, selectedDepartment, handleStepSubmitFlow],
  );

  // ステップ選択画面のSubmitハンドラ（明示的な名前付き関数）
  const handleStepSelectSubmit = useCallback(() => {
    handleStepSubmit();
  }, [handleStepSubmit]);

  // TODO保存（編集完了時のコールバック付き）
  const handleSaveTodo = useCallback(
    async (candidate: (typeof candidates)[number], index: number) => {
      const stepId = Number(selectedStep);
      return handleSaveTodoHook(
        candidate,
        index,
        stepId,
        editState.editingIndex,
        () => {
          cancelEdit();
        },
      );
    },
    [selectedStep, editState.editingIndex, handleSaveTodoHook, cancelEdit],
  );

  // TODO全保存
  const handleSaveAllTodos = useCallback(async () => {
    const stepId = Number(selectedStep);
    await handleSaveAllTodosHook(candidates, stepId);
  }, [selectedStep, candidates, handleSaveAllTodosHook]);

  // --- ローディング待ちのeffect ---
  // 部門選択後、entryManagements/entryDetailのロードが完了したらステップ追加を開始
  useEffect(() => {
    // 実行条件: PROCESSING状態 && checkingEntry && ローディング完了 && 部門選択済み
    const shouldExecute =
      modalStep === MODAL_STEP.PROCESSING &&
      processingState === PROCESSING_STATE.CHECKING_ENTRY &&
      !isLoadingEntryManagements &&
      !(existingEntryManagementId !== null && isLoadingEntryDetail) &&
      Boolean(selectedDepartment) &&
      reportAnalyzeMutation.data?.ok;

    if (!shouldExecute || !reportAnalyzeMutation.data?.ok) {
      return;
    }

    // 二重呼び出し防止チェック
    if (isAddingStepsRef.current) {
      return;
    }

    const [deptIdStr, reportType] = selectedDepartment.split('-');
    const departmentID = Number(deptIdStr);

    // reportAnalyzeMutation.dataからステップ候補を取得して追加
    isAddingStepsRef.current = true;
    addAllSteps(
      reportAnalyzeMutation.data.value.candidates,
      selectedCompanyId,
      selectedCompanyName,
      departmentID,
      reportType as ReportType,
    )
      .then((result) => {
        if (result.shouldProceedToStepSelect) {
          setModalStep(MODAL_STEP.STEP_SELECT);
        }
      })
      .catch(() => {
        setProcessingState(PROCESSING_STATE.ERROR);
        showErrorSnackBar({message: 'ステップの追加に失敗しました'});
      })
      .finally(() => {
        isAddingStepsRef.current = false;
      });

    // クリーンアップ: 依存値が変わった場合やアンマウント時にrefをリセット
    return () => {
      isAddingStepsRef.current = false;
    };
  }, [
    modalStep,
    processingState,
    isLoadingEntryManagements,
    isLoadingEntryDetail,
    existingEntryManagementId,
    selectedDepartment,
    selectedCompanyId,
    selectedCompanyName,
    reportAnalyzeMutation,
    addAllSteps,
    setProcessingState,
    setModalStep,
  ]);

  // Pre-filledモード: モーダルが開いたら自動で処理開始
  useEffect(() => {
    if (!opened || !isPreFilled || hasStartedPreFillRef.current) return;

    hasStartedPreFillRef.current = true;
    setSelectedCompanyId(propsCompanyID as number);
    setSelectedCompanyName(propsCompanyName as string);
    setSelectedDepartment(`${propsDepartmentID}-${propsReportType}`);
    setModalStep(MODAL_STEP.PROCESSING);
    setProcessingState(PROCESSING_STATE.FETCHING_STEPS);

    reportAnalyzeMutation
      .mutateAsync({
        companyID: propsCompanyID as number,
        departmentID: propsDepartmentID as number,
        reportType: propsReportType as ReportType,
      })
      .then((result) => {
        if (result.ok && result.value.candidates.length > 0) {
          setProcessingState(PROCESSING_STATE.CHECKING_ENTRY);
        } else {
          setProcessingState(PROCESSING_STATE.ERROR);
        }
      })
      .catch(() => {
        setProcessingState(PROCESSING_STATE.ERROR);
        showErrorSnackBar({message: 'ステップ候補の取得に失敗しました'});
      });
  }, [
    opened,
    isPreFilled,
    propsCompanyID,
    propsCompanyName,
    propsDepartmentID,
    propsReportType,
    reportAnalyzeMutation,
    setProcessingState,
  ]);

  // enableStepSelect=false の場合、stepSelect到達時に自動で最初のステップで生成
  useEffect(() => {
    if (
      enableStepSelect ||
      modalStep !== MODAL_STEP.STEP_SELECT ||
      addedSteps.length === 0 ||
      hasAutoGeneratedRef.current
    ) {
      return;
    }

    const firstStep = addedSteps[0];
    if (!firstStep) return;

    hasAutoGeneratedRef.current = true;
    setSelectedStep(String(firstStep.id));
    handleStepSubmit(firstStep.id);
  }, [
    enableStepSelect,
    modalStep,
    addedSteps,
    handleStepSubmit,
    hasAutoGeneratedRef,
  ]);

  // バックグラウンド生成中または結果表示中はマウント維持
  if (
    !opened &&
    !isGeneratingInBackgroundRef.current &&
    modalStep !== MODAL_STEP.RESULT
  ) {
    return null;
  }

  const renderContent = () => {
    switch (modalStep) {
      case MODAL_STEP.COMPANY_SELECT:
        return (
          <AiTodoCompanySelectContent
            onClose={handleClose}
            onSubmit={handleCompanySubmit}
            companyId={selectedCompanyId}
            onCompanySelect={(id, name) => {
              setSelectedCompanyId(id);
              setSelectedCompanyName(name);
            }}
            companyList={companyList}
          />
        );
      case MODAL_STEP.DEPARTMENT_SELECT:
        return (
          <AiTodoDepartmentSelectContent
            onBack={handleBackToCompanySelect}
            onSubmit={handleDepartmentSubmit}
            companyName={selectedCompanyName}
            departmentOptions={departmentOptions}
            selectedOption={selectedDepartment}
            onSelectChange={(value) => setSelectedDepartment(value ?? '')}
            itemComponent={DepartmentSelectItem}
            badgeComponent={leftSectionBadge}
          />
        );
      case MODAL_STEP.PROCESSING:
        return (
          <AiTodoProcessingContent
            onClose={handleClose}
            processingState={processingState}
            addedStepCount={addedStepCount}
            totalStepCount={totalStepCount}
            companyName={selectedCompanyName}
            departmentLabel={selectedDepartmentLabel}
          />
        );
      case MODAL_STEP.STEP_SELECT:
      case MODAL_STEP.GENERATING:
        return (
          <AiTodoStepSelectContent
            onBack={handleBackToDepartmentSelect}
            onSubmit={handleStepSelectSubmit}
            stepOptions={stepOptions}
            selectedStep={selectedStep}
            onStepChange={(value) => setSelectedStep(value ?? '')}
            isGenerating={modalStep === MODAL_STEP.GENERATING}
            companyName={selectedCompanyName}
            departmentLabel={selectedDepartmentLabel}
            selectedStepLabel={selectedStepLabel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* メインモーダル（企業選択〜ステップ選択・生成まで） */}
      <Modal
        opened={opened && modalStep !== MODAL_STEP.RESULT}
        onClose={handleClose}
        title="ToDoをAIで自動生成"
        centered
        size="md"
        radius={16}
        zIndex={Z_INDEX.MODAL}
        styles={{
          title: {
            fontWeight: 'bold',
          },
        }}
      >
        {renderContent()}
      </Modal>

      {/* TODO候補表示・保存モーダル */}
      <AiGeneratedTodosModal
        opened={modalStep === MODAL_STEP.RESULT && candidates.length > 0}
        onClose={handleClose}
        candidates={candidates}
        onSave={handleSaveTodo}
        onSaveAll={handleSaveAllTodos}
        isTodoSaved={isTodoSaved}
        isSaving={saveState.isSaving}
        isSavingAll={saveState.isSavingAll}
        savingIndex={saveState.savingIndex}
        editingIndex={editState.editingIndex}
        editingCandidate={editState.editingCandidate}
        onStartEdit={startEdit}
        onCancelEdit={cancelEdit}
        onUpdateCandidate={updateCandidate}
      />

      {/* SP用: ToDo一覧を開く確認モーダル */}
      <OpenTodoPageConfirmModal
        opened={showOpenTodoPageConfirm}
        onClose={handleCloseOpenTodoPageConfirm}
        onConfirm={handleOpenTodoPageConfirm}
      />
    </>
  );
};
